import { applyUtmParamsForGtm, parseStartParam } from '@utils'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DeviceOrientation, useDeviceOrientation, useFonts } from '@ds'
import {
  getCDNUrl,
  onCheckNotCoinClaimUtmParam,
  onCheckRechargeDropUtmParam,
  useFetchNotReward,
  useFetchRechargedReward,
  webApp,
} from '@shared'
import {
  LayoutTheme,
  PrologueStage,
  useAchievementsActions,
  useAchievementsStore,
  useApp,
  useAvailableQuestsActions,
  useBattlePassActions,
  useGameSettingsActions,
  useInitializeInventory,
  useLeaderboardActions,
  usePrologueActions,
  useSkins,
  useSkinsActions,
  useSkinsStore,
  useUser,
  useUserActions,
  useUserBankActions,
  useUserState,
} from '@store'

const MIN_READING_TIME = 5000 // 5 seconds to read the text in loader app

const layoutThemes = {
  [LayoutTheme.UI]: '#1C1C1C',
  [LayoutTheme.QUEST]: '#000000',
}

const useAuthentication = () => {
  const [authCheckComplete, setAuthCheckComplete] = useState(false)
  const { authorise, fetchProfile } = useUserActions()
  const isAuth = useUser.store.isAuth()
  const profile = useUserState()
  const { setPrologueStage } = usePrologueActions()

  useEffect(() => {
    if (!webApp?.initData && import.meta.env.MODE !== 'development') {
      return
    }
    const startParam = webApp?.initDataUnsafe?.start_param

    if (!isAuth) {
      const { referrerId } = parseStartParam(startParam)

      authorise(referrerId ?? undefined)
    }

    if (profile?.createdAt) {
      const { utmParams } = parseStartParam(startParam)

      onCheckRechargeDropUtmParam(utmParams)
      onCheckNotCoinClaimUtmParam(utmParams)

      if (Object.keys(utmParams).length > 0) {
        applyUtmParamsForGtm(utmParams, profile?.createdAt)
      }
    }
  }, [isAuth, profile?.createdAt])

  useEffect(() => {
    if (!isAuth) {
      return
    }

    fetchProfile().then((res) => {
      if (res?.passedPrologue === true) {
        setPrologueStage(PrologueStage.Finished)
      }
    })
    setAuthCheckComplete(true)
  }, [isAuth])

  return authCheckComplete
}

const useWebAppIntegration = (layoutTheme: LayoutTheme) => {
  const orientation = useDeviceOrientation()

  useLayoutEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]')
    meta?.setAttribute('content', layoutThemes[layoutTheme])
  }, [layoutTheme])

  useLayoutEffect(() => {
    const tgPopupAvailable = Number(webApp?.version) >= 6.2
    const tgOrientationLockAvailable = Number(webApp?.version) >= 8.0
    if (orientation === DeviceOrientation.Portrait) {
      if (tgOrientationLockAvailable) {
        webApp?.lockOrientation()
      }
    } else {
      if (tgPopupAvailable) {
        webApp?.showPopup({
          title: 'Sorry',
          message: 'Please hold your device vertically',
        })
      } else {
        console.warn('Landscape orientation is not supported!')
      }
    }
  }, [orientation])

  // useLayoutEffect(() => {
  //   const handler = webApp?.onEvent('deviceOrientationChanged', () => {
  //     const message = `alpha = ${webApp?.DeviceOrientation.alpha}\n
  //     beta = ${webApp?.DeviceOrientation.beta}\n
  //     gamma = ${webApp?.DeviceOrientation.gamma}`
  //     if (Number(webApp?.version) >= 6.2) {
  //       webApp?.showPopup({
  //         title: 'debug',
  //         message,
  //       })
  //     }
  //   })
  //   return () => {
  //     webApp?.offEvent('deviceOrientationChanged', handler)
  //   }
  // }, [])

  useEffect(() => {
    if (!webApp) {
      return
    }

    webApp.setHeaderColor(layoutThemes[layoutTheme])
    webApp.disableVerticalSwipes()

    const isMobile =
      webApp.platform === 'ios' ||
      webApp.platform === 'android' ||
      webApp.platform === 'android_x'

    if (isMobile) {
      webApp.requestFullscreen()
    }
  }, [webApp?.colorScheme, layoutTheme])
}

const useWindowSetup = () => {
  useEffect(() => {
    const setScreenWidth = () => {
      const root = document.querySelector(':root') as HTMLElement
      root?.style.setProperty('--screen-width', window.innerWidth.toString())
    }
    setScreenWidth()
    window.addEventListener('resize', setScreenWidth)

    return () => {
      window.removeEventListener('resize', setScreenWidth)
    }
  }, [])
}

const useGameSetup = () => {
  const { init: initGameSettings } = useGameSettingsActions()
  const { init: initInventory } = useInitializeInventory()
  const { loadAchievements } = useAchievementsActions()
  const { fetchSkins } = useSkinsActions()
  const skins = useSkins()

  const prefetchSkinAssets = useCallback(() => {
    if (!skins) return

    const allSkins = [
      ...skins.avatars,
      ...skins.avatarEffects,
      ...skins.spiralEffects,
      ...skins.starEffects,
    ]

    const prefetchResource = (url: string) => {
      const isImage = /\.(png|jpe?g|gif|webp|svg|avif)$/i.test(url)

      if (isImage) {
        const img = new Image()
        img.src = url
      } else {
        fetch(url).catch(() => {})
      }
    }

    allSkins.forEach((skin) => {
      if (skin.iconUrl) {
        prefetchResource(getCDNUrl(skin.iconUrl))
      }

      if (skin.appFiles) {
        prefetchResource(getCDNUrl(skin.appFiles))
      }
    })
  }, [skins])

  const init = useCallback(() => {
    initGameSettings()
    initInventory()
    loadAchievements()
    fetchSkins()
  }, [initGameSettings, initInventory, loadAchievements, fetchSkins])

  useEffect(() => {
    if (skins) {
      prefetchSkinAssets()
    }
  }, [skins, prefetchSkinAssets])

  return { init }
}

const useUserData = (isAuth: boolean) => {
  const [userDataIsLoaded, setUserDataIsLoaded] = useState(false)
  const { getBattlePass } = useBattlePassActions()
  const { getGetTapsBalance, getGetStarsBalance } = useUserBankActions()
  const { getAvailableQuests } = useAvailableQuestsActions()
  const { fetchLeaderboard } = useLeaderboardActions()
  useEffect(() => {
    if (!isAuth) {
      return
    }

    Promise.all([
      getAvailableQuests(),
      getGetTapsBalance(),
      getBattlePass(),
      getGetStarsBalance(),
      fetchLeaderboard(),
    ]).then(() => setUserDataIsLoaded(true))

    const interval = setInterval(() => getGetTapsBalance(), 60 * 1000)

    return () => clearInterval(interval)
  }, [isAuth])

  return userDataIsLoaded
}

export const useAppResources = () => {
  const [resourcesLoaded, setResourcesLoaded] = useState(false)
  const fontsLoaded = useFonts()
  const authCheckComplete = useAuthentication()
  const userDataIsLoaded = useUserData(authCheckComplete)
  const { ready } = useTranslation()
  const { isLoading: isAchievementsLoading } = useAchievementsStore()
  const { isSkinsFetching } = useSkinsStore()

  useFetchNotReward(resourcesLoaded)
  useFetchRechargedReward(resourcesLoaded)

  const layoutTheme = useApp.store.layoutTheme()
  const { init: initGame } = useGameSetup()

  useWebAppIntegration(layoutTheme)
  useWindowSetup()

  useEffect(() => {
    if (authCheckComplete) {
      initGame()
    }
  }, [authCheckComplete, initGame])

  useEffect(() => {
    if (
      fontsLoaded &&
      authCheckComplete &&
      ready &&
      userDataIsLoaded &&
      !isAchievementsLoading &&
      !isSkinsFetching
    ) {
      const timer = setTimeout(() => {
        setResourcesLoaded(true)
      }, MIN_READING_TIME)

      return () => clearTimeout(timer)
    }
  }, [
    fontsLoaded,
    authCheckComplete,
    ready,
    userDataIsLoaded,
    isAchievementsLoading,
    isSkinsFetching,
  ])

  return { resourcesLoaded }
}
